/**
 * ============================================
 * PVP: PLAYERS BY SESSION TIME (all four modes)
 * ============================================
 *
 *   PVP Local / PVP Editor        analytics level rows (GET /api/v1/multiplayer/pvp-levels)
 *                                 session = one round: level_exited, level_restarted or
 *                                 level_completed with its level_time (editor: session_time)
 *   PVP Online / PVP Editor Online match reports from the snapshot (S3 + online_pvp_data)
 *                                 session = one match report, time = match length;
 *                                 players = reports with a device id (lower bound)
 *
 * Players are distinct within a mode. Modes are stacked, never merged, because
 * the two sources identify players differently. The tab's Mode, Platform,
 * Date range and Level/Map filters all apply.
 *
 *   G33 players by session time (Day / Week / Month / All Time)   — shown
 *   Cards, G34 players/entries over time, G35 per-level table      — hidden for now (local/editor rows only)
 */

import { useMemo, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { useDateRange } from '@/context/DateRangeContext';
import { makeCountryPredicate } from '@/utils/countryRegions';
import { TimeScaleToggle, type TimeScale } from '@/components/common/TimeScaleToggle';
import type { MpPvpLevelData, MpPvpMode, MpReport } from '@/hooks/useMultiplayerAnalytics';
import { ALL, ONLINE_MODES, PVP_MODE_LABELS, PVP_MODE_ORDER, addDays, groupByPeriod, median } from './mpData';
import {
  ChartCard, ChartWindowContext, EmptyChart, Segmented, StatTile, ThemedCard, periodAxis, useChartTheme,
  type ChartWindow,
} from './MpCharts';

// Parts of this section outside the current layout spec. Flip to bring them back.
const SHOW_PVP_EXTRAS = false;

const MODE_COLORS: Record<MpPvpMode, string> = {
  local: '#3b82f6', online: '#a855f7', editor: '#f97316', editor_online: '#10b981',
};
// Same ranges the backend ships with the level rows (used when those rows are not loaded).
const DEFAULT_BUCKETS = ['0–15s', '15–30s', '30–60s', '1–2m', '2–5m', '5–10m', '10–20m', '20m+'];
const DEFAULT_UPPER: (number | null)[] = [15, 30, 60, 120, 300, 600, 1200, null];

interface PvpRow {
  dev: number;
  date: string;
  plat: string;
  level: string;
  mode: MpPvpMode;
  entries: number;
  rounds: number[];
  /** ISO-2, 'UNKNOWN', or '' on pre-v3 files */
  geo: string;
}

interface PvpStats {
  entries: number;
  entryPlayers: number;
  rounds: number;
  roundPlayers: number;
  medianSec: number;
}

function decodeRows(data: MpPvpLevelData): PvpRow[] {
  return data.rows.map((r, i) => {
    const level = data.levels[r[3]];
    // Files before version 3 carry no country.
    const ci = data.rowCountries?.[i];
    return {
      dev: r[0], date: data.dates[r[1]], plat: data.platforms[r[2]], level,
      mode: data.levelModes[level] ?? 'local', entries: r[4], rounds: r.slice(5),
      geo: ci !== undefined ? data.countries?.[ci] ?? '' : '',
    };
  });
}

function statsOf(rows: PvpRow[]): PvpStats {
  let entries = 0;
  const entryPlayers = new Set<number>();
  const roundPlayers = new Set<number>();
  const secs: number[] = [];
  for (const r of rows) {
    entries += r.entries;
    if (r.entries) entryPlayers.add(r.dev);
    if (r.rounds.length) {
      roundPlayers.add(r.dev);
      for (const s of r.rounds) secs.push(s);
    }
  }
  return { entries, entryPlayers: entryPlayers.size, rounds: secs.length, roundPlayers: roundPlayers.size, medianSec: Math.round(median(secs)) };
}

function bucketOf(seconds: number, uppers: (number | null)[]): number {
  for (let i = 0; i < uppers.length; i++) {
    const upper = uppers[i];
    if (upper === null || seconds < upper) return i;
  }
  return uppers.length - 1;
}

function fmtDuration(sec: number): string {
  if (!sec) return '—';
  if (sec < 60) return `${Math.round(sec)} s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${Math.round(sec % 60)}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function PvpLevelsSection({ data, loading, error, dataThrough, platform, scene, mode, countries, reports, className = '' }: {
  data: MpPvpLevelData | null;
  loading: boolean;
  error: string | null;
  dataThrough: string;
  platform: string;
  scene: string;
  mode: string;
  /** selected countries ([] = all) */
  countries: string[];
  /** online match reports already filtered by Mode / Platform / Level-Map (any date) */
  reports: MpReport[];
  /** grid placement of the G33 card */
  className?: string;
}) {
  const t = useChartTheme();
  const isDark = t.isDark;
  const { start: rangeStart, end: rangeEnd } = useDateRange();
  const [win, setWin] = useState<ChartWindow>('week');
  const [scale, setScale] = useState<TimeScale>('weekly');
  const [metric, setMetric] = useState<'players' | 'entries'>('players');

  const modes: MpPvpMode[] = mode === ALL ? [...PVP_MODE_ORDER] : [mode as MpPvpMode];
  const buckets = data?.buckets ?? DEFAULT_BUCKETS;
  const uppers = data?.bucketUpper ?? DEFAULT_UPPER;

  const allRows = useMemo(() => (data ? decodeRows(data) : []), [data]);
  const levelRows = useMemo(() => {
    const countryOk = makeCountryPredicate(countries);
    return allRows.filter(r =>
      countryOk(r.geo) &&
      (platform === ALL || r.plat === platform) &&
      (scene === ALL || r.level === scene) &&
      (mode === ALL || r.mode === mode));
  }, [allRows, platform, scene, mode, countries]);

  // 'range' follows the date range; Day / Week / Month end at the last complete day and,
  // like All Time, ignore the date range (same as every other chart's window).
  const windowRange = useMemo((): { start: string | null; end: string } => {
    const through = data?.dataThrough || dataThrough;
    if (win === 'range') return { start: rangeStart || null, end: rangeEnd && rangeEnd < through ? rangeEnd : through };
    if (win === 'all') return { start: null, end: through };
    return { start: addDays(through, win === 'day' ? 0 : win === 'week' ? -6 : -29), end: through };
  }, [data, dataThrough, rangeStart, rangeEnd, win]);
  const inWin = (date: string) => date <= windowRange.end && (!windowRange.start || date >= windowRange.start);

  const levelRowsInWindow = useMemo(() => levelRows.filter(r => inWin(r.date)), [levelRows, windowRange]); // eslint-disable-line react-hooks/exhaustive-deps
  const reportsInWindow = useMemo(() => reports.filter(r => inWin(r.date)), [reports, windowRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per mode: distinct players and sessions in each time range, plus totals.
  const perMode = useMemo(() => {
    const out = {} as Record<MpPvpMode, { players: Set<number>[]; sessions: number[]; totalPlayers: Set<number>; secs: number[] }>;
    PVP_MODE_ORDER.forEach(m => {
      out[m] = { players: buckets.map(() => new Set<number>()), sessions: buckets.map(() => 0), totalPlayers: new Set<number>(), secs: [] };
    });
    for (const r of levelRowsInWindow) {
      const x = out[r.mode];
      for (const s of r.rounds) {
        const b = bucketOf(s, uppers);
        x.players[b].add(r.dev);
        x.sessions[b]++;
        x.totalPlayers.add(r.dev);
        x.secs.push(s);
      }
    }
    for (const r of reportsInWindow) {
      const x = out[r.mode];
      if (!x) continue;
      const b = bucketOf(r.el, uppers);
      x.sessions[b]++;
      x.secs.push(r.el);
      if (r.dev >= 0) {
        x.players[b].add(r.dev);
        x.totalPlayers.add(r.dev);
      }
    }
    return out;
  }, [levelRowsInWindow, reportsInWindow, buckets, uppers]);

  const chartRows = useMemo(
    () => buckets.map((bucket, i) => {
      const row: Record<string, string | number> = { bucket };
      modes.forEach(m => {
        row[m] = perMode[m].players[i].size;
        row[`${m}Sessions`] = perMode[m].sessions[i];
      });
      return row;
    }),
    [buckets, modes, perMode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const firstDate = useMemo(() => {
    let first: string | null = null;
    for (const r of levelRowsInWindow) if (!first || r.date < first) first = r.date;
    for (const r of reportsInWindow) if (!first || r.date < first) first = r.date;
    return first;
  }, [levelRowsInWindow, reportsInWindow]);

  // ---- hidden extras (SHOW_PVP_EXTRAS, level rows only) ----------------------------
  const summary = useMemo(() => statsOf(levelRowsInWindow), [levelRowsInWindow]);
  const trendRows = useMemo(() => {
    if (!SHOW_PVP_EXTRAS || !data) return [];
    const inRange = levelRows.filter(r => (!rangeStart || r.date >= rangeStart) && (!rangeEnd || r.date <= rangeEnd));
    return groupByPeriod(inRange, r => r.date, scale, data.dataThrough).map(b => {
      const row: Record<string, string | number> = { label: b.label };
      (['local', 'editor'] as MpPvpMode[]).forEach(m => {
        const rows = b.items.filter(r => r.mode === m);
        row[m] = metric === 'players'
          ? new Set(rows.filter(r => r.entries).map(r => r.dev)).size
          : rows.reduce((s, r) => s + r.entries, 0);
      });
      return row;
    });
  }, [data, levelRows, rangeStart, rangeEnd, scale, metric]);
  const levelTable = useMemo(() => {
    if (!SHOW_PVP_EXTRAS) return [];
    const byLevel = new Map<string, PvpRow[]>();
    levelRowsInWindow.forEach(r => {
      const list = byLevel.get(r.level);
      if (list) list.push(r); else byLevel.set(r.level, [r]);
    });
    return Array.from(byLevel.entries())
      .map(([level, rows]) => ({ level, mode: rows[0].mode, ...statsOf(rows) }))
      .sort((a, b) => b.entries - a.entries || b.rounds - a.rounds);
  }, [levelRowsInWindow]);

  // ---- render -----------------------------------------------------------------------
  const levelModesShown = modes.some(m => !ONLINE_MODES.has(m));
  const levelDataMissing = levelModesShown && !data;
  const levelNote = loading ? 'Loading PVP Local / Editor data…'
    : error ? `PVP Local / Editor data failed to load: ${error}`
    : 'PVP Local / Editor data appears after the next multiplayer sync (backend 5.3).';

  const from = windowRange.start ?? firstDate ?? windowRange.end;
  const windowText = from === windowRange.end ? windowRange.end : `${from} – ${windowRange.end}`;
  const totalSessions = modes.reduce((s, m) => s + perMode[m].secs.length, 0);
  const modeSummary = modes
    .map(m => `${PVP_MODE_LABELS[m]}: ${perMode[m].totalPlayers.size.toLocaleString()} players, ${perMode[m].secs.length.toLocaleString()} sessions, median ${fmtDuration(Math.round(median(perMode[m].secs)))}`)
    .join(' · ');
  const th = `px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const td = `px-3 py-2 text-xs whitespace-nowrap ${isDark ? 'text-gray-200' : 'text-gray-700'}`;

  return (
    <>

      {SHOW_PVP_EXTRAS && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Players who entered" value={summary.entryPlayers.toLocaleString()} sub={windowText} />
          <StatTile label="Level entries" value={summary.entries.toLocaleString()} tone="purple" />
          <StatTile label="Players with a round" value={summary.roundPlayers.toLocaleString()} sub={`${summary.rounds.toLocaleString()} rounds`} tone="green" />
          <StatTile label="Median round time" value={fmtDuration(summary.medianSec)} tone="orange" />
        </div>
      )}

      <ChartWindowContext.Provider value={{ value: win, onChange: setWin, label: windowText }}>
      <ChartCard id="G33" title={`PVP: players by session time · ${mode === ALL ? 'all modes' : PVP_MODE_LABELS[mode]}`}
        n={totalSessions} height={280} className={className}
        footer={`${levelDataMissing ? `${levelNote} · ` : ''}PVP Local & PVP Editor: one round = one session (analytics). PVP Online & PVP Editor Online: one match = one session (match reports). ${windowText} · ${modeSummary}. Players are distinct within a mode and counted once in every time range they played in, so points can add up to more than the total. Online players are a lower bound (S3-only reports have no device id).`}>
        {chartRows.every(r => modes.every(m => !r[m])) ? (
          <EmptyChart message={levelDataMissing && !reportsInWindow.length ? levelNote : 'No sessions in this window'} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: t.text }} stroke={t.grid}
                label={{ value: 'session time', position: 'insideBottomRight', offset: -2, fill: t.text, fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} allowDecimals={false}
                label={{ value: 'players', angle: -90, position: 'insideLeft', fill: t.text, fontSize: 10 }} />
              <Tooltip contentStyle={t.tooltip}
                formatter={(v: number, name: string, item: { dataKey?: string | number; payload?: Record<string, number> }) =>
                  [`${v.toLocaleString()} players · ${(item?.payload?.[`${item?.dataKey}Sessions`] ?? 0).toLocaleString()} sessions`, name]} />
              {modes.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {modes.map(m => (
                <Line key={m} type="monotone" dataKey={m} name={PVP_MODE_LABELS[m]} stroke={MODE_COLORS[m]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
      </ChartWindowContext.Provider>

      {SHOW_PVP_EXTRAS && data && (
        <ChartCard id="G34" title={metric === 'players' ? 'Players entering PVP levels over time' : 'PVP level entries over time'}
          height={300}
          right={
            <div className="flex items-center gap-2">
              <Segmented value={metric} options={[{ value: 'players', label: 'Players' }, { value: 'entries', label: 'Entries' }]} onChange={setMetric} />
              <TimeScaleToggle scale={scale} onChange={setScale} isDark={isDark} />
            </div>
          }
          footer="PVP Local and PVP Editor. Players are distinct per period and mode. * = still filling up.">
          {trendRows.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendRows}>
                <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />
                <XAxis {...periodAxis(t)} />
                <YAxis tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} allowDecimals={false} />
                <Tooltip contentStyle={t.tooltip} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {(['local', 'editor'] as MpPvpMode[]).map(m => (
                  <Line key={m} type="monotone" dataKey={m} name={PVP_MODE_LABELS[m]} stroke={MODE_COLORS[m]} strokeWidth={2} dot={{ r: 3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      )}

      {SHOW_PVP_EXTRAS && (
        <ThemedCard isDark={isDark}>
          <div className={`px-4 py-3 border-b flex items-center gap-2 ${isDark ? 'border-gray-700/50' : 'border-gray-200'}`}>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>G35</span>
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>PVP levels · {windowText}</h3>
          </div>
          {levelTable.length === 0 ? <div className="h-24"><EmptyChart message="No PVP level activity in this window" /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={th}>Level</th>
                    <th className={th}>Mode</th>
                    <th className={th}>Entries</th>
                    <th className={th}>Players entered</th>
                    <th className={th}>Rounds</th>
                    <th className={th}>Players with a round</th>
                    <th className={th}>Median round time</th>
                  </tr>
                </thead>
                <tbody>
                  {levelTable.map(r => (
                    <tr key={r.level} className={`border-t ${isDark ? 'border-gray-700/50' : 'border-gray-100'}`}>
                      <td className={td}>{r.level}</td>
                      <td className={td}>{PVP_MODE_LABELS[r.mode]}</td>
                      <td className={td}>{r.entries.toLocaleString()}</td>
                      <td className={td}>{r.entryPlayers.toLocaleString()}</td>
                      <td className={td}>{r.rounds.toLocaleString()}</td>
                      <td className={td}>{r.roundPlayers.toLocaleString()}</td>
                      <td className={td}>{fmtDuration(r.medianSec)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ThemedCard>
      )}
    </>
  );
}
